export const getUserRole = (user) => {
  if (user?.role) {
    return user.role;
  }
  try {
    const stored = localStorage.getItem('user');
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed?.role || null;
  } catch (error) {
    return null;
  }
};

export const isAdmin = (role) => role === 'admin';
export const isManager = (role) => role === 'manager';
export const isAdminOrManager = (role) => isAdmin(role) || isManager(role);

export const isAccountingManager = (role) => role === 'accountingManager';
export const isAccountingIn = (role) => role === 'accountingIn';
export const isAccountingOut = (role) => role === 'accountingOut';

export const canViewUsers = (role) => isAdminOrManager(role);
export const canEditUsers = (role) => isAdminOrManager(role);

export const canViewCustomers = (role) =>
  !!role && ![ 'accountingManager', 'accountingIn', 'accountingOut', 'partner' ].includes(role);
export const canEditCustomers = (role) => isAdminOrManager(role);
export const canCreateCustomers = (role) => isAdminOrManager(role);

export const canViewCarriers = (role) =>
  !!role && ![ 'partner', 'accountingManager', 'accountingIn', 'accountingOut' ].includes(role);
export const canEditCarriers = (role) => isAdminOrManager(role);
export const canCreateCarriers = (role) => isAdminOrManager(role);

export const canViewPaymentsReceivable = (role) =>
  [ 'admin', 'accountingManager', 'accountingIn' ].includes(role);
export const canViewPaymentsPayable = (role) =>
  [ 'admin', 'accountingManager', 'accountingOut' ].includes(role);
export const canViewPayments = (role) =>
  canViewPaymentsReceivable(role) || canViewPaymentsPayable(role);
export const canEditPayments = (role) => isAdminOrManager(role);

export const canViewLoads = (role) =>
  !!role && ![ 'accountingManager', 'accountingIn', 'accountingOut' ].includes(role);
export const canCreateLoads = (role) =>
  [ 'admin', 'manager', 'freightBroker', 'Pre-dispatcher', 'salesAgent' ].includes(role);
export const canEditLoads = (role) =>
  [ 'admin', 'manager', 'freightBroker', 'dispatcher', 'Pre-dispatcher', 'salesAgent' ].includes(role);
export const canDeleteLoads = (role) => isAdminOrManager(role);
export const canViewLoadHistory = (role) => isAdminOrManager(role);
export const canDuplicateLoads = (role) =>
  [ 'admin', 'manager', 'freightBroker', 'salesAgent' ].includes(role);

const STATISTICS_TAB_IDS_ALL = [ 'users', 'loads', 'receivable', 'payable', 'carriers', 'customers' ];
const RESTRICTED_STATISTICS_TAB_IDS = [ 'loads', 'customers' ];
export const RESTRICTED_STATS_ROLES = [ 'bidAgent', 'salesAgent', 'Pre-dispatcher', 'freightBroker', 'dispatcher' ];
const ACCOUNTING_STATS_TAB_IDS = {
  accountingManager: [ 'receivable', 'payable' ],
  accountingIn: [ 'receivable' ],
  accountingOut: [ 'payable' ],
};

export const ACCOUNTING_ROLES = [ 'accountingManager', 'accountingIn', 'accountingOut' ];

export const getStatisticsTabIdsForRole = (role) => {
  if (ACCOUNTING_STATS_TAB_IDS[role]) return ACCOUNTING_STATS_TAB_IDS[role];
  if (RESTRICTED_STATS_ROLES.includes(role)) return RESTRICTED_STATISTICS_TAB_IDS;
  return STATISTICS_TAB_IDS_ALL;
};

export const getLoadStepAccess = (role) => {
  if (!role) {
    return { step1to4: false, step5: false, readOnly: true };
  }
  if ([ 'accountingManager', 'accountingIn', 'accountingOut' ].includes(role)) {
    return { step1to4: false, step5: false, readOnly: true };
  }
  if (role === 'partner' || role === 'bidAgent') {
    return { step1to4: false, step5: false, readOnly: true };
  }
  if (role === 'dispatcher') {
    return { step1to4: false, step5: true, readOnly: false };
  }
  if (role === 'Pre-dispatcher') {
    return { step1to4: true, step5: false, readOnly: false };
  }
  return { step1to4: true, step5: true, readOnly: false };
};

export const isPaymentNotification = (notification) => {
  const types = [
    'payment_receivable_created',
    'payment_payable_created',
    'payment_receivable_status_update',
    'payment_payable_status_update',
    'payment_status_changed',
    'payment_overdue',
    'payment_due_today',
    'payment.created',
    'payment.updated',
    'payment_payable_need_to_pay'
  ];
  return types.includes(notification?.type);
};

export const isReceivableNotification = (notification) => {
  const types = [
    'payment_receivable_created',
    'payment_receivable_status_update',
    'payment_overdue',
    'payment_due_today',
    'payment.created',
    'payment.updated'
  ];
  return types.includes(notification?.type);
};

export const isPayableNotification = (notification) => {
  const types = [
    'payment_payable_created',
    'payment_payable_status_update',
    'payment_payable_need_to_pay',
    'payment.created',
    'payment.updated'
  ];
  return types.includes(notification?.type);
};

export const isLoadNotification = (notification) => {
  const types = [
    'load_created',
    'load_updated',
    'load_status_update',
    'load_status_changed',
    'load_delivered',
    'load_assigned',
    'load.created',
    'load.updated',
    'load.status_updated',
    'load_status_update',
    'load_status_changed'
  ];
  return types.includes(notification?.type);
};

export const canViewAllNotifications = (role) => isAdminOrManager(role);

export const canViewPaymentNotifications = (role, notification) => {
  if (isAccountingManager(role)) return isPaymentNotification(notification);
  if (isAccountingIn(role)) return isReceivableNotification(notification);
  if (isAccountingOut(role)) return isPayableNotification(notification);
  if (isAdminOrManager(role)) return true;
  return false;
};

export const canViewLoadNotifications = (role, notification) => {
  if (!role) return false;
  if ([ 'accountingManager', 'accountingIn', 'accountingOut', 'partner' ].includes(role)) {
    return false;
  }
  return isLoadNotification(notification);
};

export const filterNotificationsByRole = (notifications, role) => {
  if (!Array.isArray(notifications)) return [];
  if (canViewAllNotifications(role)) return notifications;
  if (isAccountingManager(role) || isAccountingIn(role) || isAccountingOut(role)) {
    return notifications.filter((notification) => canViewPaymentNotifications(role, notification));
  }
  if (role === 'partner') return [];
  return notifications.filter((notification) => canViewLoadNotifications(role, notification));
};

export const filterActivitiesByRole = (notifications, role) => {
  if (!Array.isArray(notifications)) return [];
  if (canViewAllNotifications(role)) return notifications;
  return [];
};
